<button
  type="button"
  class="drawer-backdrop"
  [class.is-open]="open()"
  [attr.tabindex]="open() ? 0 : -1"
  aria-label="Close details"
  (click)="closed.emit()"></button>
<aside class="drawer" [class.is-open]="open()" [attr.aria-hidden]="!open()">
  @if (vm(); as vm) {
    <div class="drawer-head">
      <div class="drawer-head__l">
        <h2 class="drawer-agent">{{ vm.run.agentName }}</h2>
        <div class="drawer-ctx">{{ vm.run.contextId }} &middot; attempt {{ vm.run.attempt }}</div>
      </div>
      <div class="drawer-head__r">
        <span [class]="'status-badge status-badge--lg ' + vm.status.cls">
          <span class="status-badge__dot" [class.status-badge__dot--pulse]="vm.status.live"></span>{{ vm.status.label }}
        </span>
        <button type="button" class="drawer-close" (click)="closed.emit()" aria-label="Close">×</button>
      </div>
    </div>

    <div class="drawer-body">
      <div class="id-grid">
        <div class="id-field">
          <div class="k">Run #</div>
          <div class="v">{{ vm.run.agentRunId }}</div>
        </div>
        <div class="id-field">
          <div class="k">Agent ID</div>
          <div class="v null">NULL</div>
        </div>
        <div class="id-field">
          <div class="k">Run For Date</div>
          <div class="v" [class.null]="!vm.run.runForDate">{{ vm.run.runForDate ?? 'NULL' }}</div>
        </div>
        <div class="id-field">
          <div class="k">Context ID</div>
          <div class="v mono">{{ vm.run.contextId }}</div>
        </div>
        <div class="id-field">
          <div class="k">Attempt</div>
          <div class="v">{{ vm.run.attempt }}</div>
        </div>
        <div class="id-field">
          <div class="k">Created</div>
          <div class="v mono" [class.null]="!vm.createdText">{{ vm.createdText ?? 'NULL' }}</div>
        </div>
        <div class="id-field">
          <div class="k">Updated</div>
          <div class="v mono" [class.null]="!vm.updatedText">{{ vm.updatedText ?? 'NULL' }}</div>
        </div>
      </div>

      @if (vm.canRerun || vm.canStop || vm.stopPending) {
        <div class="drawer-actions">
          @if (vm.canStop || vm.stopPending) {
            @if (confirmingStop()) {
              <span class="stop-confirm" role="group" aria-labelledby="stop-confirm-text">
                <span id="stop-confirm-text">Stop this run? It can't be resumed.</span>
                <button type="button" class="btn-stop btn-stop--confirm" (click)="confirmStop()">Stop run</button>
                <button #keepRunningButton type="button" class="btn-rerun btn-rerun--lg" (click)="cancelStopConfirm()">Keep running</button>
              </span>
            } @else {
              <button
                #stopButton
                type="button"
                class="btn-stop"
                [disabled]="vm.stopPending"
                (click)="openStopConfirm()">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
                {{ vm.stopPending ? 'Stopping…' : 'Stop this agent run' }}
              </button>
            }
          }
          @if (vm.canRerun) {
            <button
              type="button"
              class="btn-rerun btn-rerun--lg"
              [disabled]="vm.rerunPending"
              (click)="rerunRequested.emit()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              {{ vm.rerunPending ? 'Rerunning…' : 'Rerun this agent run' }}
            </button>
          }
        </div>
      }

      @if (rerunFeedback(); as feedback) {
        <p
          class="rerun-feedback"
          [class.rerun-feedback--error]="feedback.tone === 'error'"
          [class.rerun-feedback--success]="feedback.tone === 'success'"
          [attr.role]="feedback.tone === 'error' ? 'alert' : 'status'"
          [attr.aria-live]="feedback.tone === 'error' ? 'assertive' : 'polite'">
          {{ feedback.text }}
        </p>
      }

      <div class="section-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 12h4l3 8 4-16 3 8h4" />
        </svg>
        Handoff Timeline
        @if (vm.totalText) {
          <span class="total-chip">total {{ vm.totalText }}</span>
        }
      </div>
      <div class="waterfall">
        @if (vm.waterfall) {
          <div class="waterfall__seg waterfall__seg--bus" [style.width.%]="vm.waterfall.bus">{{ vm.waterfall.busLabel }}</div>
          <div class="waterfall__seg waterfall__seg--pickup" [style.width.%]="vm.waterfall.pickup">{{ vm.waterfall.pickupLabel }}</div>
          <div
            class="waterfall__seg waterfall__seg--run"
            [class.is-failed]="vm.isFail"
            [class.waterfall__seg--open]="vm.live"
            [style.width.%]="vm.waterfall.run">
            {{ vm.waterfall.runLabel }}
          </div>
        } @else {
          <div class="waterfall__seg waterfall__seg--run waterfall__seg--full" [class.is-failed]="vm.isFail">no handoff timestamps</div>
        }
      </div>
      <div class="waterfall-legend">
        <span><span class="lgd-dot lgd-dot--bus"></span>Pipeline &rarr; Bus wait</span>
        <span><span class="lgd-dot lgd-dot--pickup"></span>Bus &rarr; Agent pickup</span>
        <span><span class="lgd-dot lgd-dot--run"></span>Agent run</span>
      </div>
      <ul class="hops">
        @for (hop of vm.hops; track hop.col) {
          <li [class]="'hop ' + hop.cls">
            <div class="hop__rail"><span class="hop__dot"></span><span class="hop__line"></span></div>
            <div>
              <div class="hop__name">{{ hop.name }}</div>
              <div class="hop__col">{{ hop.col }}</div>
              @if (hop.time) {
                <div class="hop__time">{{ hop.time }}</div>
              } @else {
                <div class="hop__time hop__time--null">not recorded (NULL)</div>
              }
            </div>
            @if (hop.delta) {
              <span class="hop__delta" [class.hop__delta--muted]="hop.delta.muted">{{ hop.delta.text }}</span>
            }
          </li>
        }
      </ul>

      <div class="section-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <path d="M22 4L12 14.01l-3-3" />
        </svg>
        Outcome
      </div>
      @if (vm.run.agentOutcome) {
        <div class="outcome-box">{{ vm.run.agentOutcome }}</div>
      } @else {
        <div class="outcome-box outcome-box--empty">No outcome recorded for this run (AgentOutcome is NULL).</div>
      }

      <div class="section-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 6h16M4 12h16M4 18h10" />
        </svg>
        Summary
      </div>
      @if (vm.run.summary) {
        <div class="summary-box">{{ vm.run.summary }}</div>
      } @else {
        <div class="summary-box summary-box--empty">No summary recorded for this run (Summary is NULL).</div>
      }
    </div>
  }
</aside>
